import { isAxiosError } from 'axios';
import { Result, err, ok } from 'neverthrow';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import axios from '@utils/axios';

import { Maybe } from 'types/UtilityTypes';
import { ApiError } from 'types/api/ApiError';
import captureException from 'utils/captureException';

import { toApiError } from './toApiError';

// What the rep asked Kit to look for in the thread.
export enum KitThreadCheckKind {
  QuoteRequest = 'quoteRequest',
  LoadBuilding = 'loadBuilding',
}

export enum KitThreadCheckStatus {
  Pending = 'pending',
  Processing = 'processing',
  Succeeded = 'succeeded',
  Failed = 'failed',
}

export type KitThreadCheckJob = {
  jobId: string;
  threadId: string;
  emailId: number;
  kind: KitThreadCheckKind;
  status: KitThreadCheckStatus;
  progress: { completed: number; total: number };
  result: Maybe<KitThreadCheckResult>;
  feedback: { helpful: Maybe<boolean>; comment: Maybe<string> };
  errorMessage: Maybe<string>;
  createdAt: string;
  startedAt: Maybe<string>;
  completedAt: Maybe<string>;
};

export type KitThreadCheckResult = {
  emailsChecked: number;
  quoteRequestIds: number[];
  suggestedLoadChangeIds: number[];
  newSuggestionCount: number;
};

const START_ERROR_MESSAGE = "Kit couldn't start checking this thread.";
const STATUS_ERROR_MESSAGE = "Couldn't check on Kit's progress.";
const FEEDBACK_ERROR_MESSAGE = "Couldn't save your feedback. Try again.";

// Starts a check, or returns the check already running for this thread and kind.
export async function startKitThreadCheck({
  threadID,
  emailID,
  kind,
}: {
  threadID: string;
  emailID: number;
  kind: KitThreadCheckKind;
}): Promise<Result<KitThreadCheckJob, ApiError>> {
  try {
    const response = await axios.post<KitThreadCheckJob>(
      `thread/${encodeURIComponent(threadID)}/kit/check`,
      { kind, emailId: emailID }
    );
    return ok(response.data);
  } catch (error) {
    return err(handleError(error, 'startKitThreadCheck', START_ERROR_MESSAGE));
  }
}

// The newest check for this thread and kind, or null when Kit never checked it.
export async function getLatestKitThreadCheck(
  threadID: string,
  kind: KitThreadCheckKind
): Promise<Result<Maybe<KitThreadCheckJob>, ApiError>> {
  try {
    const response = await axios.get<KitThreadCheckJob>(
      `thread/${encodeURIComponent(threadID)}/kit/check/latest`,
      { params: { kind } }
    );
    return ok(response.data);
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return ok(null);
    }
    return err(
      handleError(error, 'getLatestKitThreadCheck', STATUS_ERROR_MESSAGE)
    );
  }
}

export async function getKitThreadCheck(
  jobID: string
): Promise<Result<KitThreadCheckJob, ApiError>> {
  try {
    const response = await axios.get<KitThreadCheckJob>(
      `kit/check/${encodeURIComponent(jobID)}`
    );
    return ok(response.data);
  } catch (error) {
    return err(handleError(error, 'getKitThreadCheck', STATUS_ERROR_MESSAGE));
  }
}

export async function submitKitThreadCheckFeedback({
  jobID,
  helpful,
  comment,
}: {
  jobID: string;
  helpful: boolean;
  comment?: string;
}): Promise<Result<null, ApiError>> {
  try {
    const trimmedComment = comment?.trim();
    await axios.post(`kit/check/${encodeURIComponent(jobID)}/feedback`, {
      helpful,
      ...(trimmedComment ? { comment: trimmedComment } : {}),
    });
    return ok(null);
  } catch (error) {
    return err(
      handleError(error, 'submitKitThreadCheckFeedback', FEEDBACK_ERROR_MESSAGE)
    );
  }
}

// Auth errors are rethrown so the app can send the rep to sign in again.
function handleError(
  error: unknown,
  functionName: string,
  fallbackMessage: string
): ApiError {
  captureException(error, { functionName });

  if (!isAxiosError(error)) {
    return { message: fallbackMessage };
  }
  if (error.response?.status === 401) {
    throw error;
  }
  return toApiError(error, fallbackMessage, error.response?.status);
}
